import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Box, Chip, IconButton, Stack, Typography} from '@mui/material';
import {ChevronLeft, ChevronRight} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import AggregatedVariantsNexusSearch from '../../../../core/services/nexus-search/aggregated-variants';
import ObserversService from '../../../../core/services/observers';

const SCROLL_EDGE_EPSILON = 2;
/** Card product-body padding — pull arrows into this gutter. */
const CARD_SIDE_GUTTER = '0.5rem';

/**
 * Single-line horizontal chip carousel with scroll buttons.
 * Card mode (!compact): negative margin on the row so arrows sit in the card side padding.
 * Compact (datagrid): inline chevrons, no outward push.
 */
const OptionChipRow = ({
                           group,
                           selectedRaw,
                           compact = false,
                           onSelect,
                       }) => {
    const {t} = useTranslation('components', {
        keyPrefix: 'app-specific.variant-option-pickers',
    });
    const trackRef = useRef(null);
    const selectedChipRef = useRef(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    /**
     * Recomputes whether the chip track can scroll in either direction.
     */
    const updateScrollState = useCallback(() => {
        const el = trackRef.current;
        if (!el) {
            setCanScrollPrev(false);
            setCanScrollNext(false);
            return;
        }
        const {scrollLeft, scrollWidth, clientWidth} = el;
        setCanScrollPrev(scrollLeft > SCROLL_EDGE_EPSILON);
        setCanScrollNext(scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_EPSILON);
    }, []);

    /**
     * Watches track size / option list changes for overflow.
     */
    useEffect(() => {
        const el = trackRef.current;
        if (!el) {
            return undefined;
        }
        updateScrollState();
        const observer = ObserversService.newResizeObserver(() => {
            updateScrollState();
        });
        ObserversService.observeResizeObserver(observer, el);
        return () => {
            ObserversService.disconnectResizeObserver(observer);
        };
    }, [group.options, updateScrollState]);

    /**
     * When selection changes, nudge the track only if the selected chip is clipped.
     * Uses instant scrollBy from the current position — never scrollIntoView / restore-to-ref.
     */
    useLayoutEffect(() => {
        const track = trackRef.current;
        const chip = selectedChipRef.current;
        if (!track || !chip) {
            return;
        }
        const trackRect = track.getBoundingClientRect();
        const chipRect = chip.getBoundingClientRect();
        let delta = 0;
        if (chipRect.left < trackRect.left) {
            delta = chipRect.left - trackRect.left;
        } else if (chipRect.right > trackRect.right) {
            delta = chipRect.right - trackRect.right;
        }
        if (delta !== 0) {
            track.scrollBy({left: delta, behavior: 'auto'});
        }
        updateScrollState();
    }, [selectedRaw, updateScrollState]);

    /**
     * Scrolls the track by roughly 80% of its visible width.
     */
    const scrollByPage = useCallback((direction, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        const el = trackRef.current;
        if (!el) {
            return;
        }
        const delta = Math.max(el.clientWidth * 0.8, 80) * direction;
        el.scrollBy({left: delta, behavior: 'smooth'});
    }, []);

    /**
     * Prevents native focus scroll-into-view from jumping the chip track on click.
     */
    const onChipMouseDown = useCallback((event) => {
        event.preventDefault();
    }, []);

    const showScrollControls = canScrollPrev || canScrollNext;
    const buttonSize = compact ? 22 : 20;

    const arrowSx = {
        flexShrink: 0,
        width: buttonSize,
        height: buttonSize,
        p: 0,
        borderRadius: '50%',
        color: 'text.primary',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        transition: (theme) => theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
            duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
            bgcolor: 'action.hover',
            borderColor: 'primary.main',
            color: 'primary.main',
        },
        '&:focus': {
            outline: 'none',
        },
        '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
            bgcolor: 'action.selected',
            borderColor: 'primary.main',
            color: 'primary.main',
            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}33`,
        },
        '&.Mui-disabled': {
            opacity: 0.35,
            borderColor: 'divider',
            color: 'text.disabled',
        },
    };

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                minWidth: 0,
                // Card: expand into product-body side padding so arrows hug the card edges.
                ...(!compact
                    ? {
                        width: `calc(100% + 2 * ${CARD_SIDE_GUTTER})`,
                        mx: `-${CARD_SIDE_GUTTER}`,
                    }
                    : {
                        width: '100%',
                    }),
            }}
        >
            {
                showScrollControls &&
                <IconButton
                    size={'small'}
                    aria-label={t('scrollPrev')}
                    disabled={!canScrollPrev}
                    onClick={(e) => scrollByPage(-1, e)}
                    sx={arrowSx}
                >
                    <ChevronLeft sx={{fontSize: compact ? 18 : 16}}/>
                </IconButton>
            }
            <Box
                ref={trackRef}
                onScroll={updateScrollState}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 0.75,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {display: 'none'},
                    py: 0.25,
                    // Slight inset so chips don’t collide with the side-pushed arrows.
                    ...(!compact && showScrollControls ? {px: 0.25} : {}),
                }}
            >
                {
                    group.options.map((option) => {
                        const selected = selectedRaw === option.id;
                        return (
                            <Chip
                                key={`${group.id}-${JSON.stringify(option.id)}`}
                                ref={selected ? selectedChipRef : undefined}
                                size={'small'}
                                label={option.title}
                                clickable
                                color={selected ? 'primary' : 'default'}
                                variant={selected ? 'filled' : 'outlined'}
                                onMouseDown={onChipMouseDown}
                                onClick={(e) => onSelect(group.id, option.id, e)}
                                sx={{
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    minWidth: compact ? 36 : 40,
                                    height: compact ? 28 : 30,
                                    fontWeight: selected ? 700 : 500,
                                    ...(selected
                                        ? {}
                                        : {
                                            bgcolor: 'background.paper',
                                            borderColor: 'divider',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'action.hover',
                                            },
                                        }),
                                }}
                            />
                        );
                    })
                }
            </Box>
            {
                showScrollControls &&
                <IconButton
                    size={'small'}
                    aria-label={t('scrollNext')}
                    disabled={!canScrollNext}
                    onClick={(e) => scrollByPage(1, e)}
                    sx={arrowSx}
                >
                    <ChevronRight sx={{fontSize: compact ? 18 : 16}}/>
                </IconButton>
            }
        </Box>
    );
};

/**
 * Hierarchical variance option pickers for an aggregated product card / grid cell.
 */
const VariantOptionPickers = ({
                                  info,
                                  varianceProperties = [],
                                  onAggregatedSelectionChange,
                                  compact = false,
                              }) => {
    const {t} = useTranslation('components', {
        keyPrefix: 'app-specific.variant-option-pickers',
    });

    const selectedMember = useMemo(() => {
        if (!info?.members?.length) {
            return null;
        }
        return info.members.find((m) => m.partNo === info.selectedPartNo) ?? info.members[0];
    }, [info]);

    const optionGroups = useMemo(() => {
        if (!info?.isAggregated || !info?.members?.length || !varianceProperties.length) {
            return [];
        }
        return AggregatedVariantsNexusSearch.buildHierarchicalOptions(
            info.members,
            varianceProperties,
            selectedMember?.variantDetails,
        );
    }, [info, varianceProperties, selectedMember]);

    /**
     * Handles selecting an option on a variance dimension.
     */
    const onOptionSelect = useCallback((propertyId, rawValue, event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (!info?.members?.length) {
            return;
        }
        const next = AggregatedVariantsNexusSearch.selectOption(
            info,
            propertyId,
            rawValue,
            varianceProperties,
        );
        onAggregatedSelectionChange?.(next);
    }, [info, onAggregatedSelectionChange, varianceProperties]);

    if (!info?.isAggregated || optionGroups.length === 0) {
        return null;
    }

    return (
        <Stack
            spacing={compact ? 0.75 : 1}
            sx={{width: '100%', minWidth: 0}}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            {
                info.variantCount > 1 &&
                <Typography variant={'caption'} color={'text.secondary'} fontWeight={600}>
                    {t('optionsCount', {count: info.variantCount})}
                </Typography>
            }
            {
                optionGroups.map((group) => (
                    <Box key={group.id} sx={{minWidth: 0}}>
                        <Typography
                            variant={'caption'}
                            fontWeight={600}
                            color={'text.secondary'}
                            sx={{mb: 0.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.4}}
                        >
                            {group.title}
                        </Typography>
                        <OptionChipRow
                            group={group}
                            selectedRaw={selectedMember?.variantDetails?.[group.id]?.raw}
                            compact={compact}
                            onSelect={onOptionSelect}
                        />
                    </Box>
                ))
            }
        </Stack>
    );
};

export default VariantOptionPickers;
